import { PButtonPure } from "@porsche-design-system/components-react"
import type { JSX } from "react"
import { useCallback, useMemo } from "react"
import { useIntl } from "react-intl"
import { ActionItemResponsive } from "src/components"
import type { ActionItemProps } from "src/components"
import { useEditStageContextForStage } from "src/context/editStageContext/useEditStageContext"
import type { VehicleDetailsStage } from "src/screens/vehicleDetails/common/VehicleDetailsStage"
import { vehicleDetailsMessages } from "src/screens/vehicleDetails/common/vehicleDetails.messages"

interface ClickHandler {
	fetch: () => Promise<void>
	isLoading: boolean
}

export const useEditActionButton = (
	stageId: VehicleDetailsStage,
	editAction: ActionItemProps["view"] | undefined,
	clickHandler: ClickHandler | undefined,
): JSX.Element | undefined => {
	const { mode, setEditStage } = useEditStageContextForStage<VehicleDetailsStage>(stageId)
	const { formatMessage } = useIntl()

	const handleEditClicked = useCallback(() => {
		if (clickHandler) {
			clickHandler.fetch().then(() => setEditStage(stageId))
		} else {
			setEditStage(stageId)
		}
	}, [setEditStage, stageId, clickHandler])
	const handleCancelClicked = useCallback(() => setEditStage(undefined), [setEditStage])

	return useMemo(() => {
		if (!editAction) {
			return undefined
		}
		return mode !== "edit" ? (
			<ActionItemResponsive
				key="edit"
				data-testid={`${stageId}_edit-button`}
				onClick={handleEditClicked}
				isLoading={clickHandler?.isLoading}
				icon="edit"
				view={{
					disabledHint:
						mode === "readOnly"
							? formatMessage(vehicleDetailsMessages.editDisabledHint)
							: editAction.disabledHint,
					label: editAction.label,
				}}
			/>
		) : (
			<PButtonPure type="button" key="close" icon="close" hideLabel onClick={handleCancelClicked} />
		)
	}, [editAction, formatMessage, mode, handleCancelClicked, handleEditClicked, stageId, clickHandler?.isLoading])
}
